import { Stack, styled, Typography } from "@mui/material";
import { LinkTabs } from "./link-tabs";
import { RouteObject } from "react-router";
import { TPageHeadRoute } from "../page-head";
import { cacheService } from "../../../../Core/Api/services";
import { PlatformMode } from "../../../../Core/Api/Types/platform-mode";
import { UserHasAcls, UserHasLicence } from "../../../../Utils/helpers";

interface IPageHeadLinkTabsProps {
  route: TPageHeadRoute;
}

export type TLinkTab = RouteObject & {
  element: React.ReactElement & ILinkTabProps;
};

export interface ILinkTabProps {
  linkTitle: string;
}

function isJSXRouteElement(element: React.ReactElement) {
  return (element as React.ReactElement).props !== undefined;
}

function isLinkTabElement(element: React.ReactElement) {
  if (!isJSXRouteElement(element)) return false;
  return (
    // First child is a link tab
    (element!.props as any).hasOwnProperty("linkTitle")
  );
}

function isHidden(element: React.ReactElement): boolean {
  const { acls, licence, accessLevel, hide, platformModes, aclMatch } = element.props as any;

  if (hide) return true;
  if (acls && !UserHasAcls(acls, aclMatch)) return true;
  if (licence && !UserHasLicence(licence)) return true;
  if (accessLevel && cacheService.GetGroup()?.aclAssetAccessLevel !== accessLevel) return true;
  if (platformModes && !platformModes.includes(cacheService.GetPlatformMode())) return true;

  return false;
}

function isPlatformSpecificLinkTab(element: React.ReactElement, platformMode: PlatformMode) {
  if (!isJSXRouteElement(element)) return false;
  return (
    (element!.props as any).hasOwnProperty(platformMode) &&
    isJSXRouteElement((element!.props as any)[platformMode]) &&
    (element!.props as any)[platformMode].props.hasOwnProperty("linkTitle")
  );
}

const PageHeadLinkTabs: React.FC<IPageHeadLinkTabsProps> = (props) => {
  const { route } = props;
  const platform = cacheService.GetPlatformMode();

  const linksFinal = route.children?.reduce((acc, childRoute) => {
    if (isHidden(childRoute.element as React.ReactElement)) return acc;

    if (isLinkTabElement(childRoute.element as React.ReactElement)) {
      acc.push(childRoute as TLinkTab);
    }

    if (isJSXRouteElement(childRoute.element as React.ReactElement)) {
      if (isPlatformSpecificLinkTab(childRoute.element as React.ReactElement, platform)) {
        acc.push({
          ...childRoute,
          element: ((childRoute.element as React.ReactElement).props as any)[platform] as TLinkTab["element"],
        });
      }
    }

    return acc;
  }, [] as TLinkTab[]);

  return (
    <>
      {route.children && (
        <Stack direction="row" gap={1} alignItems="baseline">
          <Typography variant="subtitle1" fontWeight={600}>
            {route.element.props.pageTitle}
          </Typography>
          {linksFinal! && linksFinal.length > 0 && <LinkTabs parentPath={route.path} links={linksFinal} />}
        </Stack>
      )}
    </>
  );
};

export default PageHeadLinkTabs;

const PageHeadLinkTabsStyled = styled(Stack, { shouldForwardProp: (prop) => prop !== "variant" })<{
  variant?: string;
}>(({ theme }) => ({}));
